import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ActionIcon, Button, Group, Loader, ScrollArea, SimpleGrid, Skeleton, Text, TextInput, rem } from '@mantine/core';
import {
  IconArrowLeft, IconArrowsRightLeft, IconBuilding, IconBuildingSkyscraper, IconCheck, IconSearch, IconX,
} from '@tabler/icons-react';
import { useMemo, useState, type CSSProperties } from 'react';
import { useDebouncedValue } from '@mantine/hooks';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useAuth } from '@/app/providers/AuthProvider';
import { altreAziende } from '@/features/tenants/tenantAccess';
import { entraNellAzienda } from '@/features/tenants/switchTenant';
import { fetchList } from '@/shared/api/jsonapi/client';
import { JsonApiHttpError } from '@/shared/api/jsonapi/errors';
import { MaeShell, MaePageHead } from '@/shared/desktop/MaeShell';
import { StatCard } from '@/shared/components/StatCard';
import { mw, MW_CARD_SHADOW } from '@/shared/mobile/tokens';
import type { Denormalized } from '@/shared/api/jsonapi/parser';
import { inkOn } from '@/shared/theme/generaToken';

export const Route = createFileRoute('/_app/switch-tenant')({ component: SwitchTenantPage });

interface TenantRow extends Denormalized { type: 'tenants'; name?: string; businessName?: string }

const CARD: CSSProperties = {
  border: `1px solid ${mw.line}`, borderRadius: 16, background: mw.surface, boxShadow: MW_CARD_SHADOW,
};

function initials(s?: string | null) {
  const parts = (s ?? '').trim().split(/\s+/).filter(Boolean);
  return (parts.map((w) => w[0]).slice(0, 2).join('') || 'T').toUpperCase();
}

function SwitchTenantPage() {
  const navigate = useNavigate();
  const { decoded, associatedTenants, loginWithEnvelope } = useAuth();
  const currentTenantId = decoded?.tenantId;
  const queryClient = useQueryClient();

  const [query, setQuery] = useState('');
  const [debounced] = useDebouncedValue(query.trim(), 300);
  const [switching, setSwitching] = useState<string | null>(null);

  const currentName = (associatedTenants ?? []).find((t) => t.tenantId === currentTenantId)?.name ?? 'Workspace';

  // ── Le tue aziende (switch reale) — filtro client-side ──
  const myTenants = useMemo(() => {
    const list = associatedTenants ?? [];
    if (!debounced) return list;
    const q = debounced.toLowerCase();
    return list.filter((t) => t.name.toLowerCase().includes(q));
  }, [associatedTenants, debounced]);

  /** Le proprie aziende, per sottrarle al perimetro: dal `v1.152.0` il server le include. */
  const memberships = useMemo(
    () => new Set((associatedTenants ?? []).map((t) => t.tenantId)),
    [associatedTenants],
  );

  /*
   * ── Altre aziende — l'elenco lo decide il SERVER ──
   *
   * ⚠️ **Da qui si ENTRA, e basta.** Fino al 09-09-2026 questa sezione faceva un'altra cosa: non
   * cambiava il token, accendeva un «override» tenuto in sessione che infilava
   * `filter[tenant.id]` in ogni chiamata JSON:API. Erano due meccanismi diversi sulla stessa
   * pagina, e il secondo era un'invenzione nostra — Lares non ce l'ha. Quello che Lares fa per
   * amministrare una controllata **senza entrarci** è passare l'azienda scelta come filtro della
   * singola query (`equals(tenant.id,'X')`, `core/erp-role.js`), da un selettore di pagina: una
   * scelta esplicita, dove serve, invece di uno stato globale che nessuno vede.
   *
   * Cosa costava l'override: una lista nera di risorse da escludere («aggiungi un type qui se
   * vedi 400»), nessun effetto sugli endpoint custom — che leggono il tenant dal token e
   * rispondevano **vuoto** senza dire niente — e l'organigramma di un'azienda mostrato a chi ne
   * stava amministrando un'altra.
   *
   * Se il server rifiuta l'ingresso risponde 401 e lo si dice: chi amministra un gruppo senza
   * poterci entrare lo fa da Organizzazione, che l'azienda la nomina in ogni richiesta.
   *
   * ⚠️ Qui c'erano due regole nostre, e dal BE `v1.152.0` sono entrambe sbagliate:
   *
   * 1. un gate sul claim `canManageSubTenants`, che spegneva la sezione. Ma il perimetro ora
   *    compone appartenenza + discendenti + gruppo holding + `ITenantEntryPolicy`, e la policy
   *    include `isDeveloper` e i ruoli viaggiatori: chi ha quelli **entra** e da qui non vedeva il
   *    bottone. Nella direzione opposta, `canSeeAllTenants` è stato potato da `Staff`, `Admin` e
   *    `Marketing`, che quindi vedevano un contenitore vuoto;
   * 2. un filtro `parentTenant.id = corrente` per i non-superadmin, che **restringeva ai figli
   *    diretti** un insieme che il server calcola su tutto il gruppo: nipoti e sorelle di gruppo
   *    sparivano a chi ha diritto di vederle.
   *
   * Il perimetro è un calcolo del server e ha una sola definizione là: qui si chiede e si mostra
   * quello che torna. La sezione esiste se l'insieme, togliendo le proprie aziende, non è vuoto.
   */
  const crossQ = useQuery({
    queryKey: ['switch-tenant-perimetro', currentTenantId, debounced],
    enabled: Boolean(currentTenantId),
    staleTime: 60_000,
    queryFn: async () => {
      const res = await fetchList<TenantRow>('tenants', {
        filter: debounced ? { name: { like: debounced } } : undefined,
        sort: ['name'],
        page: { size: 100 },
      });
      return res.data;
    },
  });

  /**
   * Entra davvero nell'azienda scelta.
   *
   * ## Perché prima non entrava
   *
   * `change-tenant` risponde 200 con una coppia di token **nuova**, legata all'azienda di
   * destinazione. Quella risposta veniva buttata via e si chiamava `refresh()`, che rifà
   * `GET /api/refresh-token` col refresh token in `localStorage` — cioè quello **vecchio**.
   * Il server, quando l'header `tenantId` non c'è, ricava l'azienda dal claim del token che
   * gli mandi (`RefreshTokenController`): il claim era quello di partenza, quindi rispondeva
   * con un token per l'azienda di prima. Il cambio avveniva e veniva disfatto subito dopo,
   * nella stessa funzione — da fuori sembrava che il bottone non facesse niente.
   *
   * ## Perché si ricarica la pagina
   *
   * Il token nuovo non basta: la cache di TanStack Query è ancora piena delle risposte
   * dell'azienda di prima, e sono dati di un'**altra** azienda, non dati vecchi da rinfrescare.
   * Ricaricare è l'unico modo che non richiede di ricordarsi, uno per uno, tutti i posti che
   * tengono stato derivato dal tenant. È anche ciò che fanno il wizard di setup e il FE Ember,
   * dove il cambio azienda funziona.
   *
   * L'avviso si lascia scritto per **dopo** il ricaricamento: mostrarlo prima significa
   * mostrarlo a una pagina che sta per sparire.
   */
  const enter = async (tenantId: string, name: string) => {
    if (!tenantId || tenantId === currentTenantId) return;
    setSwitching(tenantId);
    try {
      await entraNellAzienda({ tenantId, nome: name, applica: loginWithEnvelope });
    } catch (err) {
      /* Dal BE `v1.152.0` questo può fallire con **401** dove prima rispondeva 200 emettendo il
         token (chiusura della porta di `isOwner`). Non c'è rischio di logout involontario: la
         chiamata è `anonymous`, quindi il refresh-on-401 dell'interceptor non la vede. Il testo del
         server è inglese e senza `code`: lo traduce `TENANT_ENTRY_DENIED` in `errors.ts`. */
      const message = err instanceof JsonApiHttpError ? err.getUserMessage() : 'Cambio azienda fallito';
      notifications.show({ color: 'red', message });
    } finally {
      setSwitching(null);
    }
  };


  const hasMyTenants = (associatedTenants?.length ?? 0) > 1;
  const other = useMemo(
    () => altreAziende(crossQ.data, currentTenantId, memberships),
    [crossQ.data, currentTenantId, memberships],
  );
  const crossCount = other.length;

  /*
   * La sezione esiste o no in base al perimetro **nudo**, quello senza ricerca: la ricerca filtra
   * dentro una sezione che esiste, non la fa esistere. Senza questa distinzione un utente senza
   * altre aziende, appena scrive nella casella, si vedrebbe comparire un contenitore che gli dice
   * «nessun risultato» per un insieme che per lui non esiste.
   *
   * Il perimetro nudo non costa una seconda chiamata: è la query che gira al primo render, quindi è
   * già in cache quando la ricerca cambia la chiave.
   */
  const bare = queryClient.getQueryData<TenantRow[]>(['switch-tenant-perimetro', currentTenantId, '']);
  const scopeHasOthers = altreAziende(bare, currentTenantId, memberships).length > 0;
  /* In caricamento e in errore la sezione si mostra comunque: un 500 non deve assomigliare a «non
     ci sono altre aziende» (DESIGN_SYSTEM §Stati e guida). */
  const otherSection = scopeHasOthers || (!debounced && (crossQ.isLoading || crossQ.isError));
  const showsNothing = !hasMyTenants && !otherSection;

  return (
    <MaeShell>
      <MaePageHead
        icon={<IconArrowsRightLeft size={22} />}
        title="Cambia azienda"
        sub="Cerca e seleziona l'azienda su cui vuoi lavorare."
        tone="bordeaux"
        actions={
          <Button variant="light" color="dark" leftSection={<IconArrowLeft size={16} />} onClick={() => void navigate({ to: '/' })}>
            Indietro
          </Button>
        }
      />

      <SimpleGrid cols={{ base: 1, sm: 3 }} spacing={14} mb={18}>
        <StatCard label="Azienda attuale" value={currentName} icon={IconBuilding} color="brand" />
        <StatCard label="Le tue aziende" value={associatedTenants?.length ?? 0} icon={IconBuildingSkyscraper} color="brand" />
        {otherSection && <StatCard label="Altre aziende" value={crossCount} icon={IconArrowsRightLeft} color="blue" loading={crossQ.isLoading} />}
      </SimpleGrid>

      {!showsNothing && (
        <div style={{ ...CARD, padding: '14px 16px', marginBottom: 16 }}>
          <TextInput
            size="md"
            placeholder="Cerca azienda per nome…"
            value={query}
            onChange={(e) => setQuery(e.currentTarget.value)}
            leftSection={<IconSearch size={18} />}
            rightSection={query ? (
              <ActionIcon variant="subtle" color="gray" aria-label="Pulisci ricerca" onClick={() => setQuery('')}><IconX size={16} /></ActionIcon>
            ) : null}
            data-autofocus
          />
        </div>
      )}

      {showsNothing && (
        <div style={{ ...CARD, padding: '36px 20px', textAlign: 'center' }}>
          <span style={{ display: 'inline-flex', width: 52, height: 52, borderRadius: 14, background: mw.brandTint, color: mw.brand, alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}><IconBuilding size={26} /></span>
          <Text style={{ fontSize: 'var(--fs-body-lg)', fontWeight: 700, color: mw.ink900 }}>Nessun&apos;altra azienda disponibile</Text>
          <Text style={{ fontSize: 'var(--fs-compact)', color: mw.ink500, marginTop: 4 }}>Il tuo account è associato a una sola azienda, e il sistema non ti apre nessun&apos;altra.</Text>
        </div>
      )}

      {/* ── Le tue aziende ── */}
      {hasMyTenants && (
        <div style={{ ...CARD, padding: 20, marginBottom: 16 }}>
          <SectionHead icon={<IconBuilding size={18} />} tone="bordeaux" title="Le tue aziende" count={myTenants.length} />
          {myTenants.length === 0 ? (
            <EmptyRow text={`Nessuna azienda trovata per «${debounced}».`} />
          ) : (
            <SimpleGrid cols={{ base: 1, xs: 2, sm: 3, lg: 4 }} spacing={14} mt={14}>
              {myTenants.map((t) => {
                const isCurrent = t.tenantId === currentTenantId;
                return (
                  <TenantTile
                    key={t.tenantId}
                    name={t.name}
                    tone="bordeaux"
                    state={isCurrent ? 'current' : 'select'}
                    loading={switching === t.tenantId}
                    onClick={() => void enter(t.tenantId, t.name)}
                  />
                );
              })}
            </SimpleGrid>
          )}
        </div>
      )}

      {/* ── Le altre aziende del perimetro ── */}
      {otherSection && (
        <div style={{ ...CARD, padding: 20 }}>
          <SectionHead icon={<IconArrowsRightLeft size={18} />} tone="azzurro" title="Altre aziende" count={crossCount} />

          {crossQ.isError ? (
            <div style={{ marginTop: 14, border: `1px solid ${mw.dangerLine}`, background: mw.dangerBg, borderRadius: 12, padding: '14px 16px', display: 'flex', gap: 12, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
              <Text style={{ fontSize: 'var(--fs-compact)', color: mw.danger }}>Errore nel caricamento delle aziende.</Text>
              <Button size="xs" variant="light" color="red" onClick={() => void crossQ.refetch()}>Riprova</Button>
            </div>
          ) : crossQ.isLoading ? (
            <SimpleGrid cols={{ base: 1, xs: 2, sm: 3, lg: 4 }} spacing={14} mt={14}>
              {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} h={128} radius={14} />)}
            </SimpleGrid>
          ) : crossCount === 0 ? (
            <EmptyRow text={debounced ? `Nessuna azienda trovata per «${debounced}».` : 'Non ci sono altre aziende in cui puoi entrare.'} />
          ) : (
            <ScrollArea.Autosize mah={540}>
              <SimpleGrid cols={{ base: 1, xs: 2, sm: 3, lg: 4 }} spacing={14} mt={14}>
                {other.map((t) => {
                  const name = t.name ?? t.businessName ?? `Tenant ${t.id.slice(0, 8)}`;
                  return (
                    <TenantTile
                      key={t.id}
                      name={name}
                      tone="azzurro"
                      state={t.id === currentTenantId ? 'current' : 'select'}
                      loading={switching === t.id}
                      onClick={() => void enter(t.id, name)}
                    />
                  );
                })}
              </SimpleGrid>
            </ScrollArea.Autosize>
          )}
          {!debounced && crossCount >= 100 && (
            <Text style={{ fontSize: 'var(--fs-compact)', color: mw.ink500, marginTop: 10 }}>Mostrate le prime 100. Usa la ricerca per trovarne altre.</Text>
          )}
        </div>
      )}
    </MaeShell>
  );
}

function SectionHead({ icon, tone, title, count, chip }: { icon: React.ReactNode; tone: 'bordeaux' | 'azzurro'; title: string; count: number; chip?: string }) {
  const c = tone === 'azzurro' ? { bg: mw.accentTint, fg: mw.accentText } : { bg: mw.brandTint, fg: mw.brand };
  return (
    <Group gap={10}>
      <span style={{ width: 32, height: rem(32), borderRadius: 9, background: c.bg, color: c.fg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{icon}</span>
      <Text style={{ fontSize: 'var(--fs-body-lg)', fontWeight: 700, color: mw.ink900 }}>{title}</Text>
      <span style={{ fontSize: 'var(--fs-compact)', fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: mw.surface2, color: mw.ink500 }}>{count}</span>
      {chip && <span style={{ fontSize: 'var(--fs-compact)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '.08em', padding: '2px 8px', borderRadius: 999, background: mw.accentTint, color: mw.accentText }}>{chip}</span>}
    </Group>
  );
}

function EmptyRow({ text }: { text: string }) {
  return <Text style={{ fontSize: 'var(--fs-compact)', color: mw.ink500, padding: '16px 2px' }}>{text}</Text>;
}

function TenantTile({ name, tone, state, loading, onClick }: {
  name: string;
  tone: 'bordeaux' | 'azzurro';
  state: 'current' | 'select';
  loading?: boolean;
  onClick: () => void;
}) {
  const interactive = state === 'select' && !loading;
  const av = tone === 'azzurro' ? { bg: mw.accent } : { bg: mw.brand };
  return (
    <button
      className={interactive ? 'mw-pressable' : undefined}
      onClick={interactive ? onClick : undefined}
      disabled={!interactive}
      /* Il motivo nell'attributo nativo e non in un `MaeGate`: avvolgere la card la
         sbiadirebbe al 45%, cioè spegnerebbe visivamente proprio quella su cui l'utente si
         trova. Qui la card resta leggibile e dice perché non si clicca. */
      title={state === 'current'
        ? 'Sei già in questa azienda.'
        : loading ? 'Sto entrando…' : undefined}
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, textAlign: 'center',
        border: `1px solid ${state === 'current' ? mw.brand : mw.line}`,
        borderRadius: 16, background: mw.surface, boxShadow: MW_CARD_SHADOW, padding: '18px 14px',
        cursor: interactive ? 'pointer' : 'default', fontFamily: 'inherit', width: '100%',
        opacity: state === 'current' ? 0.9 : 1,
      }}
    >
      <span style={{ width: rem(48), height: rem(48), borderRadius: 13, background: av.bg, color: inkOn(av.bg), fontSize: 'var(--fs-lead)', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        {loading ? <Loader size="sm" color="#fff" /> : initials(name)}
      </span>
      <Text style={{ fontSize: 'var(--fs-compact)', fontWeight: 600, color: mw.ink900, minHeight: '2.4em', display: 'flex', alignItems: 'center' }} lineClamp={2}>{name}</Text>
      {state === 'current' && <span style={{ fontSize: 'var(--fs-compact)', fontWeight: 700, padding: '3px 10px', borderRadius: 999, background: mw.brandTint, color: mw.brand, display: 'inline-flex', alignItems: 'center', gap: 4 }}><IconCheck size={12} /> Attuale</span>}
      {state === 'select' && <span style={{ fontSize: 'var(--fs-compact)', fontWeight: 600, color: tone === 'azzurro' ? mw.accentText : mw.brand }}>Seleziona →</span>}
    </button>
  );
}
